import { ROLES, hasRole } from "@/lib/roles"
import type { Appointment } from "@/types"

/**
 * Who gets the desktop 15-minute appointment popup — the pure half of
 * components/appointment-reminder-scheduler.tsx, kept free of React so
 * scripts/appointment-reminder-scope.test.mjs can run it under node --test.
 */

// The minimal slice of the current user that reminder-scoping needs.
export type ReminderUser = {
  id: number | string | undefined
  roles: string[] | undefined
  /**
   * `appointment_reminders_owner_only` (Radiance 260930): when set, a reminder
   * rings its owner and nobody else — no team-lead copy, no admin/manager
   * "everything". Wins over `teamScope`.
   */
  ownerOnly?: boolean
  /**
   * `appointment_reminders_team_scope` (Radiance 260924): when set, the role
   * matrix below is bypassed — a reminder rings its owner and the lead(s) of
   * the owner's teams, nobody else, admins included.
   */
  teamScope?: boolean
  /** Ids (as strings) of the members of every team this user leads. */
  ledMemberIds?: string[]
}

export type ReminderAppointment = Pick<Appointment, "user_id" | "status" | "type">

/**
 * Task 4D — role/type-aware reminder scoping.
 *
 * Decides whether a single appointment should arm a staff toast + chime for the
 * current user. `typesEnabled` is the clinic-level `typed_events_enabled`
 * flag: when OFF we reproduce the pre-Task-4D behavior EXACTLY (own scheduled
 * appointments only, any type). When ON we scope by the user's role(s).
 *
 * Filtering matrix (flag ON) — for a multi-role user the MOST PERMISSIVE
 * matching rule wins (rules are unioned; an "everything" role short-circuits):
 *
 *   role          | assignee scope | type scope
 *   --------------+----------------+--------------------------------
 *   admin/manager | any            | any            → everything
 *   other/unknown | any            | any            → everything
 *   (no roles)    | any            | any            → everything
 *   coordinator   | any assignee   | clinic_visit (or null/legacy)
 *   frontdesk     | any assignee   | clinic_visit (or null/legacy)
 *   agent         | own only       | any
 *   doctor        | own only       | clinic_visit (or null/legacy)
 *
 * `coordinator` is the canonical successor to `frontdesk` (D2,
 * docs/ROLE-CONSOLIDATION-260725.md) and gets the identical rule. `frontdesk`
 * stays until the data rename lands.
 *
 * ⚠️ This NARROWS coordinator. Until now `coordinator` was not a scoped role, so
 * the `totalRoleCount > scopedRoleCount` clause below gave every coordinator
 * user everything-access — all appointments, all types. They now see whole-clinic
 * VISITS only, i.e. exactly what a frontdesk user sees. That is the intended port
 * of frontdesk's behaviour, and it affects real users on Dentex and Navident.
 *
 * `type` is nullable — a null/absent type is legacy data and treated as
 * `clinic_visit`. Cancelled appointments never arm a reminder on the flag-ON
 * path (the flag-OFF path already excludes them via the status filter, so its
 * behavior is left untouched). `user_id` is coerced to string on both sides:
 * User.id is typed string but is a number at runtime (Laravel BigInt), while
 * apt.user_id is typed number — see 37-RESEARCH.md Pitfall 1.
 */
export function shouldRemind(apt: ReminderAppointment, user: ReminderUser, typesEnabled: boolean): boolean {
  const isOwn = String(apt.user_id) === String(user.id)

  if (user.ownerOnly) {
    if (apt.status === "cancelled") return false
    return isOwn
  }

  if (user.teamScope) {
    if (apt.status === "cancelled") return false
    return isOwn || (user.ledMemberIds ?? []).includes(String(apt.user_id))
  }

  // Flag OFF → pre-Task-4D behavior: own appointments only, any type.
  if (!typesEnabled) return isOwn

  // Flag ON: never arm a reminder for a cancelled appointment.
  if (apt.status === "cancelled") return false

  const roles = user.roles
  const isDoctor = hasRole(roles, ROLES.DOCTOR)
  const isFrontdesk = hasRole(roles, ROLES.FRONTDESK)
  const isCoordinator = hasRole(roles, ROLES.COORDINATOR)
  const isAgent = hasRole(roles, ROLES.AGENT)

  // "Everything" access = admin, manager, a user carrying any role outside the
  // four scoped roles (accountant, lab_technician, custom slugs...), or a user
  // with no roles at all. Counting scoped-vs-total lets a mixed role set like
  // [doctor, accountant] fall through to full awareness (most permissive wins).
  const scopedRoleCount =
    (isDoctor ? 1 : 0) + (isFrontdesk ? 1 : 0) + (isCoordinator ? 1 : 0) + (isAgent ? 1 : 0)
  const totalRoleCount = roles?.length ?? 0
  const hasEverythingAccess =
    hasRole(roles, ROLES.ADMIN) ||
    hasRole(roles, ROLES.MANAGER) ||
    totalRoleCount === 0 ||
    totalRoleCount > scopedRoleCount
  if (hasEverythingAccess) return true

  // null/legacy type counts as a clinic visit.
  const isClinicVisit = (apt.type ?? "clinic_visit") === "clinic_visit"

  // Union of the scoped-role allowances (most permissive rule wins).
  if (isCoordinator && isClinicVisit) return true // any assignee, visits only
  if (isFrontdesk && isClinicVisit) return true // any assignee, visits only
  if (isAgent && isOwn) return true             // own, any type
  if (isDoctor && isOwn && isClinicVisit) return true // own, visits only
  return false
}

// Whether the fetch itself must pull OTHER users' appointments (coordinator and
// frontdesk see every assignee's visits; everything-access roles see all
// appointments). When false we keep the original user-scoped query, so the
// flag-OFF path is byte-for-byte identical to before Task 4D.
//
// MUST mirror shouldRemind's matrix — a role that can be reminded about another
// user's appointment has to have that appointment fetched in the first place.
export function needsAllAssignees(user: ReminderUser, typesEnabled: boolean): boolean {
  // Owner only: nobody else's appointments, ever.
  if (user.ownerOnly) return false
  // Team scope: only a team lead needs anyone else's appointments.
  if (user.teamScope) return (user.ledMemberIds?.length ?? 0) > 0
  if (!typesEnabled) return false
  const roles = user.roles
  const isDoctor = hasRole(roles, ROLES.DOCTOR)
  const isFrontdesk = hasRole(roles, ROLES.FRONTDESK)
  const isCoordinator = hasRole(roles, ROLES.COORDINATOR)
  const isAgent = hasRole(roles, ROLES.AGENT)
  const scopedRoleCount =
    (isDoctor ? 1 : 0) + (isFrontdesk ? 1 : 0) + (isCoordinator ? 1 : 0) + (isAgent ? 1 : 0)
  const totalRoleCount = roles?.length ?? 0
  const hasEverythingAccess =
    hasRole(roles, ROLES.ADMIN) ||
    hasRole(roles, ROLES.MANAGER) ||
    totalRoleCount === 0 ||
    totalRoleCount > scopedRoleCount
  return hasEverythingAccess || isCoordinator || isFrontdesk
}
